import _ from 'lodash';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompData } from 'types/documentServices';
import type { CompRef, ViewModes } from '@wix/document-services-types';
import constants from '#packages/constants';
import {
  getColorValueByRole,
  type ColorName,
  type ColorPalette,
  COLOR_ROLES,
  isTextCompData,
  COLOR_REGEX,
  TEXT_HEX_STYLE_REGEX,
  HEX_REGEX,
  extractHexFromStyleAttr,
  extractTextOrBackgroundColorFromClassAttr,
  ColorMatch,
  TEXT_COLOR_BACKGROUND_COLOR_REGEX,
  getColorNameByRole,
} from '../../../colors';
import {
  type CompType,
  type PathPriorityLists,
  COMPS_WIRING_CONFIG,
  SITE_FONTS,
  type PathPriority,
  THEME_STYLE_ADD_PANEL,
} from './config/compsWiringConfig';
import {
  replaceColorsInStyleProps,
  wrapColorWithClassAttr,
  updateButtonsColorsInStyleData,
  updateDataInPropertiesSource,
  wrapColorWithClassAtributeByPropertyType,
  isInsideRepeater,
} from '../utils';
import type { ButtonPathLists } from './config/getButtonPathPriorityLists';
import { getAllMatchesFromStyleProps } from '../../utils';

const { COMP_TYPES } = constants;

export const updateColorsInStyleData = (
  styleData: CompData,
  pathPriorityLists: PathPriorityLists,
  allColors: ColorPalette,
  themeColors: ColorName[],
) => {
  const isTextFlow = isTextCompData(styleData);
  _.forOwn(
    pathPriorityLists,
    (wiringPriorityList: PathPriority, path: string) => {
      let currentPathValue = _.get(styleData, path);

      if (!currentPathValue || typeof currentPathValue === 'number') {
        return;
      }

      // for the cases when priority list depends on content
      if (typeof wiringPriorityList === 'function') {
        wiringPriorityList = wiringPriorityList(currentPathValue);
      }
      const matchToHexMappers = [
        {
          getHex: (c: string) => allColors[c as ColorName],
          regex: isTextFlow ? TEXT_COLOR_BACKGROUND_COLOR_REGEX : COLOR_REGEX,
          textFlowExtractor: extractTextOrBackgroundColorFromClassAttr,
        },
        {
          getHex: (h: string) => h,
          regex: isTextFlow ? TEXT_HEX_STYLE_REGEX : HEX_REGEX,
          textFlowExtractor: extractHexFromStyleAttr,
        },
      ];

      const defaultReplacer = (newColor: string) => () => newColor;

      const matchReplacer = (colorProp: ColorMatch | string) => {
        return isTextFlow
          ? () => {
              return typeof colorProp === 'string'
                ? wrapColorWithClassAttr(colorProp)
                : wrapColorWithClassAtributeByPropertyType(colorProp);
            }
          : defaultReplacer(colorProp as string);
      };

      matchToHexMappers.forEach(({ regex, getHex, textFlowExtractor }) => {
        const matches = _.uniq(
          getAllMatchesFromStyleProps(currentPathValue, regex),
        );

        matches.forEach((match: string) => {
          const colorMatch = isTextFlow ? textFlowExtractor(match) : match;

          const colorNameFromMatch =
            typeof colorMatch === 'string' ? colorMatch : colorMatch.colorName;

          const colorFromMatchHex = getHex(colorNameFromMatch);

          const updateTheColorInStyleProps = (newColor: string) => {
            let valueToSet: ColorMatch | string = newColor;
            if (colorMatch instanceof ColorMatch) {
              valueToSet = (
                colorMatch as ColorMatch
              ).setNewColorIndexFromColorName(newColor as ColorName);
            }
            currentPathValue = replaceColorsInStyleProps(
              currentPathValue,
              matchReplacer(valueToSet),
              match,
            );

            _.set(styleData, path, currentPathValue);
          };
          let colorToConnect;
          const priorityList = [
            ...(wiringPriorityList as unknown as COLOR_ROLES[]),
          ];

          if (isTextFlow) {
            // If there are no links in text so it should not be wired to links
            const hasLinks = styleData.linkList.length > 0;

            if (hasLinks) {
              // We add LIN role as highest priority role for text with links
              priorityList.unshift(COLOR_ROLES.LINK);
            }
          }

          const colorToWire = priorityList.find((role: COLOR_ROLES) => {
            return colorFromMatchHex === getColorValueByRole(allColors, role);
          }) as unknown as ColorName;

          if (colorToWire) {
            colorToConnect = getColorNameByRole(
              colorToWire as COLOR_ROLES,
            ) as string;
          } else {
            // if no match found in priority list, check theme colors
            colorToConnect = themeColors.find(
              (color) => allColors[color] === colorFromMatchHex,
            );
          }

          if (colorToConnect) {
            updateTheColorInStyleProps(colorToConnect as string);
          }
        });
      });
    },
  );

  if (styleData.type === 'ComponentStyle') {
    updateDataInPropertiesSource(styleData);
  }
};

const updateStyleData = ({
  compType,
  styleData,
  allColors,
  pathPriorityLists,
  themeColors,
}: {
  compType: CompType;
  styleData: Record<string, any>;
  allColors: Partial<ColorPalette>;
  pathPriorityLists: PathPriorityLists | ButtonPathLists[];
  themeColors: ColorName[];
}) => {
  if (compType === COMP_TYPES.SITE_BUTTON) {
    updateButtonsColorsInStyleData(
      styleData,
      allColors,
      pathPriorityLists as ButtonPathLists[],
    );
  } else {
    updateColorsInStyleData(
      styleData,
      pathPriorityLists as PathPriorityLists,
      allColors,
      themeColors,
    );
  }
};

const getMobileOnlyComponentRefs = (VIEW_MODES: ViewModes): CompRef[] => {
  const mobileOnlyTypes = [
    'BACK_TO_TOP_BUTTON',
    'MENU_AS_CONTAINER',
    'MENU_AS_CONTAINER_EXPANDABLE_MENU',
    'MENU_AS_CONTAINER_TOGGLE',
    'TINY_MENU',
  ];

  return mobileOnlyTypes.map((id) => ({
    id,
    type: VIEW_MODES.MOBILE,
  })) as CompRef[];
};

/**
 * Performs advanced wiring for all components depending on their type and current hex color value
 */
export const runAutoWiring = async (editorAPI: EditorAPI) => {
  await editorAPI.documentServices.transactions.runAndWaitForApproval(
    async () => {
      const regularComponents = editorAPI.components.getAllComponents();
      const mobileOnlyLayouControlsComponent = getMobileOnlyComponentRefs(
        editorAPI.viewMode.VIEW_MODES,
      );
      const allComps = [
        ...regularComponents,
        ...mobileOnlyLayouControlsComponent,
      ];

      const allColors = editorAPI.theme.colors.getAll();
      const themeColors = editorAPI.theme.colors.getVisibleThemeColorsKeys();

      const performWiring = (
        compType: CompType | typeof SITE_FONTS,
        compRef?: CompRef | string,
      ) => {
        const { getApi, pathPriorityLists } =
          COMPS_WIRING_CONFIG[compType as CompType];

        const styleApi = getApi(editorAPI);
        const styleData = compRef ? styleApi.get(compRef) : styleApi.getAll();

        if (
          !styleData ||
          // We should skip any updates for repeater nested items becuase
          // it's overrides data in different order and will use wrong one https://jira.wixpress.com/browse/WEED-32298
          (compRef &&
            typeof compRef !== 'string' &&
            isInsideRepeater(editorAPI, compRef))
        ) {
          return;
        }

        updateStyleData({
          compType,
          styleData,
          allColors,
          pathPriorityLists,
          themeColors,
        });

        if (compRef) {
          styleApi.update(compRef, styleData);
        } else {
          styleApi.update(styleData);
        }
      };

      performWiring(SITE_FONTS);

      allComps.forEach((compRef: CompRef) => {
        const compType = editorAPI.components.getType(compRef) as CompType;

        if (COMPS_WIRING_CONFIG.hasOwnProperty(compType)) {
          performWiring(compType, compRef);
        }
      });

      for (const [compType, compIds] of Object.entries(THEME_STYLE_ADD_PANEL)) {
        const config = COMPS_WIRING_CONFIG[compType as CompType];
        const { pathPriorityLists } = config;
        const styleAPI = editorAPI.theme.styles;

        for (const compId of compIds) {
          const styleData = styleAPI.get(compId);

          if (!styleData) {
            continue;
          }

          updateStyleData({
            compType,
            styleData,
            allColors,
            pathPriorityLists,
            themeColors,
          });

          styleAPI.update(compId, styleData);
        }
      }

      await editorAPI.waitForChangesAppliedAsync();
    },
  );
};
